import * as tslib_1 from "tslib";
import { BaseControl } from "@cloudpivot/form/src/common/controls/base-control";
import { AI_POLISH_API_URL, chatStream } from "../api";
/** 未在设计器中配置"角色设定"时的默认系统消息。 */
const AI_POLISH_DEFAULT_DESCRIPTION = "你是一名专业的文本润色助手。请在保持原意的前提下，将用户提供的文本润色得更专业、清晰、通顺，直接输出润色后的文本，不要添加任何解释或前缀。";
/** 接口异常时对用户展示的提示文案；接口原始报文只写入控制台，不渲染到页面上。 */
const AI_POLISH_REQUEST_ERROR = "AI 润色服务暂时不可用，请稍后重试。";
/**
 * AI 润色控件的 PC 与移动端共用控制逻辑。
 *
 * 控件始终润色自身的当前值：点击"AI 润色"弹出润色弹窗，AI 结果写入弹窗草稿
 * （dialogText），点击"应用"后才写回当前控件数据项（polishedText）。
 */
export class AIPolishControl extends BaseControl {
    constructor() {
        super(...arguments);
        /** 表单输入框当前内容（直接编辑或点击"应用"后写回的数据项值）。 */
        this.polishedText = "";
        /** 润色弹窗内展示/编辑的 AI 结果草稿；点击"应用"前不会改动 polishedText。 */
        this.dialogText = "";
        /** 是否正在请求或展示 AI 润色结果。 */
        this.loading = false;
        /** 最近一次前置校验的提示（仅"请先填写原始文本。"这类可操作的表单文案）。 */
        this.errorMessage = "";
        /** 最近一次 AI 请求是否失败，用于弹窗状态文案（接口异常不写进 errorMessage）。 */
        this.requestFailed = false;
        /**
         * 接口异常时在弹窗内展示的产品文案。
         * 弹窗打开时移动端 toast 会被 h3-dialog 盖住（toast z-index 1999），
         * 因此弹窗内需要一条持久可见的失败说明，但不回显后端原始报文。
         */
        this.requestErrorText = AI_POLISH_REQUEST_ERROR;
        /** 用于忽略失效请求结果的递增版本号。 */
        this.requestVersion = 0;
    }
    /** 获取设计器配置的润色提示词。 */
    get prompt() {
        return this.controlOpts.prompt || "请在保持原意的前提下，将以下内容润色得更专业、清晰、易于评审理解。";
    }
    /** 获取角色设定（作为 system 消息）；未配置时使用默认描述。 */
    get description() {
        return (this.controlOpts.description || "").trim() || AI_POLISH_DEFAULT_DESCRIPTION;
    }
    /** 获取配置的采样温度（0~2）；未配置或非法时返回 undefined（不传，由服务端默认）。 */
    get temperature() {
        const value = Number(this.controlOpts.temperature);
        if (!Number.isFinite(value)) {
            return undefined;
        }
        return Math.min(2, Math.max(0, value));
    }
    /** 获取配置的最大输出 token 数；未配置或非法时返回 undefined（不传，由服务端默认）。 */
    get maxTokens() {
        const value = Number(this.controlOpts.maxTokens);
        if (!Number.isFinite(value) || value <= 0) {
            return undefined;
        }
        return Math.floor(value);
    }
    /** 获取待润色的文本：当前控件自身的值。 */
    get sourceText() {
        return this.stringifyValue(this.ctrl && this.ctrl.value);
    }
    /** 打开弹窗前校验是否存在可润色的文本；为空时提示并阻止打开弹窗。 */
    validateSourceBeforeOpen() {
        if (this.sourceText.trim()) {
            return true;
        }
        this.errorMessage = "请先填写原始文本。";
        this.notifyError(this.errorMessage);
        return false;
    }
    /** 初始化当前润色文本。 */
    mounted() {
        this.polishedText = this.stringifyValue(this.ctrl && this.ctrl.value);
    }
    /** 销毁控件时使未完成请求失效。 */
    beforeDestroy() {
        this.requestVersion += 1;
    }
    /** 在表单值变化后同步润色结果，避免覆盖正在生成的内容。 */
    handleValueChange(value) {
        if (!this.loading) {
            this.polishedText = this.stringifyValue(value);
        }
    }
    /**
     * 调用 AI 大模型流式对话接口（SSE），将增量内容逐块渲染到弹窗润色结果草稿中。
     * @param polishSource 可选：指定本次要润色的文本；不传时取当前控件自身值（首次润色）。
     * @returns 是否成功取得并展示本次润色结果。
     */
    generate(polishSource) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            // 重新润色以当前润色结果（可能已被用户手动调整）为输入，而非首次原始文本；
            // 结果框为空时（首次请求失败、或用户手动清空）回退到控件自身的原始文本重试，
            // 否则空入参会遮蔽 this.sourceText，误报"请先填写原始文本。"。
            const source = polishSource && polishSource.trim() ? polishSource : this.sourceText;
            const sourceText = source.trim();
            if (!sourceText) {
                this.errorMessage = "请先填写原始文本。";
                this.notifyError(this.errorMessage);
                return false;
            }
            const version = ++this.requestVersion;
            this.loading = true;
            this.requestFailed = false;
            this.errorMessage = "";
            this.dialogText = "";
            try {
                const request = {
                    description: this.description,
                    // 提示词作为 user 消息，原始文本拼入其中一并发送
                    prompt: `${this.prompt}\n\n原始文本：\n${sourceText}`,
                };
                // 设计器配置了采样温度 / 最大输出 token 数时随请求发送，否则交由模型服务端默认
                const temperature = this.temperature;
                if (temperature !== undefined) {
                    request.temperature = temperature;
                }
                const maxTokens = this.maxTokens;
                if (maxTokens !== undefined) {
                    request.maxTokens = maxTokens;
                }
                const fullText = yield chatStream(AI_POLISH_API_URL, request, (delta) => {
                    // 只渲染当前请求的增量内容，忽略已失效请求
                    if (version === this.requestVersion) {
                        this.dialogText += delta;
                    }
                });
                if (version !== this.requestVersion) {
                    return false;
                }
                if (!fullText.trim()) {
                    throw new Error("AI 服务未返回可应用的文本内容。");
                }
                this.dialogText = fullText;
                return true;
            }
            catch (error) {
                if (version === this.requestVersion) {
                    // 接口异常只用轻提示 + 弹窗状态文案反馈，不回显后端原始报文（含内部错误码/请求 id）
                    console.error("[AIPolish] 润色请求失败：", error);
                    this.requestFailed = true;
                    this.notifyError(AI_POLISH_REQUEST_ERROR);
                }
                return false;
            }
            finally {
                if (version === this.requestVersion) {
                    this.loading = false;
                }
            }
        });
    }
    /** 将当前润色结果写回 AI 润色控件对应的表单数据项。 */
    applyPolishedText() {
        const value = this.polishedText.trim();
        if (!value) {
            return;
        }
        this.setValue(value);
        this.polishedText = value;
    }
    /** 将表单字段值统一转换为可发送给 AI 服务的文本。 */
    stringifyValue(value) {
        if (value === undefined || value === null) {
            return "";
        }
        if (Array.isArray(value)) {
            return value.join("，");
        }
        return String(value);
    }
    /**
     * 轻提示：移动端运行态没有 antd Message，用 h3-mobile-vue 的 toast；
     * PC 端沿用 antd 的 $message。
     */
    notifyError(message) {
        const vm = this;
        if (vm.$h3 && vm.$h3.toast && typeof vm.$h3.toast.show === "function") {
            vm.$h3.toast.show({
                text: message,
                // 短文案才配图标，与移动端既有 toast 约定保持一致
                iconType: message.length < 8 ? "close" : "",
                autoHide: true,
                mask: false,
            });
            return;
        }
        if (vm.$message && vm.$message.error) {
            vm.$message.error(message);
        }
    }
}
